import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useHistory } from "react-router-dom";
import CustomButton from "../../../../../components/CustomButton";
import { SectionLoader } from "../../../../../components/PageLoader";
import CustomSearchInput from "../../../../../components/CustomSearchInput";
import idealTalentsService from "../../../../../services/idealTalentsService";
import cogoToast from "cogo-toast";
import "./idealTalentstwo.css";

const IdealTalents = () => {
  const history = useHistory();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [talents, setTalents] = useState([]);
  const [inviting, setInviting] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [sortOrder, setSortOrder] = useState("desc"); 
  const [selectedTalents, setSelectedTalents] = useState([]);
  const [bulkInviting, setBulkInviting] = useState(false);
  const [page] = useState(1);
  const [limit] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const cooldownTimerRef = useRef(null);

  const COOLDOWN_HOURS = 72;

  const computeCooldowns = useCallback((talentList) => {
    const now = new Date();
    return talentList.map((t) => {
      if (!t.invited_at) return { ...t, can_invite: true, cooldown_remaining: null };
      const invitedTime = new Date(t.invited_at);
      const elapsedMs = now - invitedTime;
      const elapsedHours = elapsedMs / (1000 * 60 * 60);
      const remaining = COOLDOWN_HOURS - elapsedHours;
      if (remaining <= 0) {
        return { ...t, can_invite: true, cooldown_remaining: null };
      }
      return { ...t, can_invite: false, cooldown_remaining: remaining, invited: true };
    });
  }, []);

  useEffect(() => {
    fetchIdealTalents();
  }, [id]);

  useEffect(() => {
    if (cooldownTimerRef.current) clearInterval(cooldownTimerRef.current);

    const hasAnyCooldown = talents.some((t) => t.invited_at && !t.can_invite);
    if (!hasAnyCooldown) return;

    cooldownTimerRef.current = setInterval(() => {
      setTalents((prev) => computeCooldowns(prev));
    }, 60000);

    return () => {
      if (cooldownTimerRef.current) clearInterval(cooldownTimerRef.current);
    };
  }, [talents.length, computeCooldowns]);

  const USE_DUMMY = false;
  const DUMMY_TALENTS = [
    {
      id: 1, job_id: parseInt(id), talent_id: 524, profile_id: 789,
      name: "Michael Oluseye", title: "Senior Backend Engineer", years_xp: 5, country_id: 160,
      total_score: 85, skills_score: 42, achievement_score: 28, title_match_score: 15, culture_fit_score: 18,
      recommendation: "Strong technical background with 5 years of experience.",
      matched_skills: ["Python", "JavaScript", "Node.js", "PostgreSQL", "Docker", "AWS"],
      invited: false, invited_at: null, can_invite: true, cooldown_remaining: null,
      image_url: "https://res.cloudinary.com/workbrook-hash/image/upload/v1760446178/Uploads/pgbz5likbz26sifsehgu.png",
    },
    {
      id: 2, job_id: parseInt(id), talent_id: 497, profile_id: 562,
      name: "Sarah Johnson", title: "Full Stack Developer", years_xp: 4, country_id: 160,
      total_score: 78, skills_score: 38, achievement_score: 25, title_match_score: 15, culture_fit_score: null,
      recommendation: "Solid full-stack skills with strong JavaScript expertise.",
      matched_skills: ["JavaScript", "React", "Node.js", "MongoDB"],
      invited: true, invited_at: "2026-01-30T14:22:10.000000Z", can_invite: false, cooldown_remaining: 48,
      image_url: "https://res.cloudinary.com/workbrook-hash/image/upload/v1760446178/Uploads/pgbz5likbz26sifsehgu.png",
    },
    {
      id: 3, job_id: parseInt(id), talent_id: 301, profile_id: 410,
      name: "Chinedu Okafor", title: "DevOps Engineer", years_xp: 6, country_id: 160,
      total_score: 72, skills_score: 35, achievement_score: 22, title_match_score: 10, culture_fit_score: 5,
      recommendation: "Experienced DevOps engineer with cloud infrastructure expertise.",
      matched_skills: ["AWS", "Docker", "Kubernetes", "CI/CD", "Terraform"],
      invited: false, invited_at: null, can_invite: true, cooldown_remaining: null,
      image_url: "https://res.cloudinary.com/workbrook-hash/image/upload/v1760446178/Uploads/pgbz5likbz26sifsehgu.png",
    },
    {
      id: 4, job_id: parseInt(id), talent_id: 615, profile_id: 720,
      name: "Amina Bello", title: "Frontend Developer", years_xp: 3, country_id: 160,
      total_score: 65, skills_score: 30, achievement_score: 20, title_match_score: 10, culture_fit_score: 5,
      recommendation: "Promising frontend developer with React expertise.",
      matched_skills: ["React", "JavaScript", "CSS", "TypeScript"],
      invited: false, invited_at: null, can_invite: true, cooldown_remaining: null,
      image_url: "https://res.cloudinary.com/workbrook-hash/image/upload/v1760446178/Uploads/pgbz5likbz26sifsehgu.png",
    },
    {
      id: 5, job_id: parseInt(id), talent_id: 820, profile_id: 915,
      name: "David Adeyemi", title: "Software Architect", years_xp: 8, country_id: 160,
      total_score: 92, skills_score: 45, achievement_score: 30, title_match_score: 12, culture_fit_score: 5,
      recommendation: "Highly experienced architect with excellent leadership skills.",
      matched_skills: ["System Design", "Python", "Java", "AWS", "Microservices"],
      invited: false, invited_at: null, can_invite: true, cooldown_remaining: null,
      image_url: "https://res.cloudinary.com/workbrook-hash/image/upload/v1760446178/Uploads/pgbz5likbz26sifsehgu.png",
    },
    {
      id: 6, job_id: parseInt(id), talent_id: 333, profile_id: 440,
      name: "Fatima Yusuf", title: "Data Engineer", years_xp: 4, country_id: 160,
      total_score: 58, skills_score: 28, achievement_score: 18, title_match_score: 8, culture_fit_score: 4,
      recommendation: "Solid data engineering background with Python and SQL skills.",
      matched_skills: ["Python", "SQL", "Spark", "Airflow"],
      invited: true, invited_at: "2026-02-04T10:00:00.000000Z", can_invite: false, cooldown_remaining: 24,
      image_url: "https://res.cloudinary.com/workbrook-hash/image/upload/v1760446178/Uploads/pgbz5likbz26sifsehgu.png",
    },
  ];

  const fetchIdealTalents = async () => {
    try {
      setLoading(true);

      if (USE_DUMMY) {
        setTalents(DUMMY_TALENTS);
        setTotalCount(DUMMY_TALENTS.length);
        setLoading(false);
        return;
      }

      const response = await idealTalentsService.fetchIdealTalents(id, page, limit);
      
      if ((response.status === true || response.status === "success") && response.data) {
        const talentData = response.data["0"] || response.data.data || (Array.isArray(response.data) ? response.data : []);
        const normalized = talentData.map((t) => ({
          ...t,
          invited: !!t.invited,
        }));
        setTalents(computeCooldowns(normalized));
        setTotalCount(response.data.total_item_count || normalized.length);
      } else {
        setTalents([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error("Error fetching ideal talents:", error);
      const status = error?.response?.status;
      if (status === 401) {
        cogoToast.error("Session expired. Please log in again.", { position: "top-center", hideAfter: 4 });
      } else if (status === 403) {
        cogoToast.error("You don't have access to this job's ideal talents", { position: "top-center", hideAfter: 4 });
      } else if (status === 404) {
        cogoToast.error("Job not found", { position: "top-center", hideAfter: 4 });
      } else {
        cogoToast.error(error?.response?.data?.message || "Failed to fetch ideal talents", { position: "top-center", hideAfter: 4 });
      }
      setTalents([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const updateTalentAsInvited = (talentId) => {
    setTalents((prev) =>
      computeCooldowns(
        prev.map((t) =>
          t.talent_id === talentId
            ? { ...t, invited: true, invited_at: new Date().toISOString() }
            : t
        )
      )
    );
  };

  const handleInvite = async (talent) => {
    if (!talent.can_invite) {
      cogoToast.warn(`Cannot invite ${talent.name}. Cooldown remaining: ${talent.cooldown_remaining} hours`, { position: "top-center", hideAfter: 3 });
      return;
    }

    setInviting((prev) => ({ ...prev, [talent.talent_id]: true }));

    if (USE_DUMMY) {
      setTimeout(() => {
        updateTalentAsInvited(talent.talent_id);
        setInviting((prev) => ({ ...prev, [talent.talent_id]: false }));
        cogoToast.success(`Invitation email sent to ${getFirstName(talent.name)}`, { position: "top-center", hideAfter: 3 });
      }, 800);
      return;
    }

    try {
      const response = await idealTalentsService.inviteTalents(id, [talent.talent_id]);
      
      if (response.status === true || response.status === "success") {
        const { sent, skipped_cooldown, errors } = response.data;
        
        if (sent && sent.length > 0) {
          cogoToast.success(`Invitation email sent to ${getFirstName(talent.name)}`, { position: "top-center", hideAfter: 3 });
          updateTalentAsInvited(talent.talent_id);
        }
        
        if (skipped_cooldown && skipped_cooldown.length > 0) {
          const skipped = skipped_cooldown[0];
          cogoToast.warn(`${skipped.name} is in cooldown. ${skipped.hours_remaining} hours remaining`, { position: "top-center", hideAfter: 4 });
        }
        
        if (errors && errors.length > 0) {
          cogoToast.error(errors[0].reason, { position: "top-center", hideAfter: 4 });
        }
      }
    } catch (error) {
      console.error("Error inviting talent:", error);
      cogoToast.error(error?.response?.data?.message || "Failed to send invitation", { position: "top-center", hideAfter: 4 });
    } finally {
      setInviting((prev) => ({ ...prev, [talent.talent_id]: false }));
    }
  };

  const handleBulkInvite = async () => {
    if (selectedTalents.length === 0) {
      cogoToast.error("Please select at least one talent", { position: "top-center", hideAfter: 3 });
      return;
    }

    const invitableIds = selectedTalents.filter((tid) => {
      const t = talents.find((tal) => tal.talent_id === tid);
      return t && t.can_invite;
    });

    const skippedCount = selectedTalents.length - invitableIds.length;

    if (invitableIds.length === 0) {
      cogoToast.warn("All selected talents are in cooldown", { position: "top-center", hideAfter: 3 });
      return;
    }

    setBulkInviting(true);

    if (USE_DUMMY) {
      setTimeout(() => {
        invitableIds.forEach((tid) => updateTalentAsInvited(tid));
        let message = `Invitations sent: ${invitableIds.length}`;
        if (skippedCount > 0) message += `, Skipped (cooldown): ${skippedCount}`;
        cogoToast.success(message, { position: "top-center", hideAfter: 4 });
        setSelectedTalents([]);
        setBulkInviting(false);
      }, 1000);
      return;
    }

    try {
      const response = await idealTalentsService.inviteTalents(id, selectedTalents);
      
      if (response.status === true || response.status === "success") {
        const { sent, skipped_cooldown, errors } = response.data;
        
        if (sent && sent.length > 0) {
          sent.forEach((s) => updateTalentAsInvited(s.talent_id));
          cogoToast.success(`${sent.length} invitation${sent.length > 1 ? "s" : ""} sent successfully!`, { position: "top-center", hideAfter: 4 });
        }

        if (skipped_cooldown && skipped_cooldown.length > 0) {
          cogoToast.warn(`${skipped_cooldown.length} talent${skipped_cooldown.length > 1 ? "s" : ""} skipped (cooldown)`, { position: "top-center", hideAfter: 4 });
        }

        if (errors && errors.length > 0) {
          cogoToast.error(`${errors.length} invitation${errors.length > 1 ? "s" : ""} failed`, { position: "top-center", hideAfter: 4 });
        }

        setSelectedTalents([]);
      }
    } catch (error) {
      console.error("Error inviting talents:", error);
      cogoToast.error(error?.response?.data?.message || "Failed to send invitations", { position: "top-center", hideAfter: 4 });
    } finally {
      setBulkInviting(false);
    }
  };

  const handleSelectTalent = (talentId) => {
    setSelectedTalents((prev) =>
      prev.includes(talentId)
        ? prev.filter((id) => id !== talentId)
        : [...prev, talentId]
    );
  };

  const handleSelectAll = () => {
    if (selectedTalents.length === filteredAndSortedTalents.length) {
      setSelectedTalents([]);
    } else {
      setSelectedTalents(filteredAndSortedTalents.map((t) => t.talent_id));
    }
  };

  // Filter talents based on search term
  const filteredTalents = talents.filter((talent) => {
    const searchLower = searchTerm.toLowerCase();
    const name = talent.name || "";
    const title = talent.title || "";
    return (
      name.toLowerCase().includes(searchLower) ||
      title.toLowerCase().includes(searchLower)
    );
  });

  // Sort talents by match score
  const filteredAndSortedTalents = [...filteredTalents].sort((a, b) => {
    const scoreA = a.total_score || 0;
    const scoreB = b.total_score || 0;
    return sortOrder === "desc" ? scoreB - scoreA : scoreA - scoreB;
  });

  const getFirstName = (name) => {
    if (!name) return "Unknown";
    return name.split(" ")[0];
  };

  return (
    <div className="ideal-talents-container">
      <div style={{ position: "relative", width: "100%" }}>
        <CustomSearchInput handleChange={(e) => setSearchTerm(e.target.value)} value={searchTerm} />
        {searchTerm && (
          <button
            onClick={() => setSearchTerm("")}
            style={{
              position: "absolute",
              right: "12px",
              top: "50%",
              transform: "translateY(-50%)",
              background: "none",
              border: "none",
              cursor: "pointer",
              fontSize: "18px",
              color: "#999",
              padding: "4px 8px",
              lineHeight: 1,
            }}
          >
            &times;
          </button>
        )}
      </div>

      {/* Filter Bar */}
      <div style={{
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: "12px",
        marginBottom: "16px",
        marginTop: "12px",
        padding: "12px 16px",
        border: "1px solid #e0e0e0",
        borderRadius: "8px",
        backgroundColor: "#f8f9fa",
      }}>
        <select
          className="sort-dropdown"
          value={sortOrder}
          onChange={(e) => setSortOrder(e.target.value)}
          style={{
            minWidth: "170px",
            borderRadius: "6px",
            border: "1px solid #ccc",
            padding: "10px 12px",
            fontSize: "14px",
            cursor: "pointer",
            backgroundColor: "#fff",
            outline: "none",
          }}
        >
          <option value="desc">Highest to Lowest</option>
          <option value="asc">Lowest to Highest</option>
        </select>
      </div>

      {!loading && totalCount > 0 && (
        <div style={{ fontSize: "13px", color: "#666", marginBottom: "8px" }}>
          Showing {filteredAndSortedTalents.length} of {totalCount} ideal talent{totalCount !== 1 ? "s" : ""}
        </div>
      )}

      {loading && <SectionLoader />}
      {!loading && (!talents || talents.length === 0) && (
        <div className="d-flex align-items-center justify-content-center mb-3">
          <p className="text-center">No ideal talents found</p>
        </div>
      )}

      {!loading && talents.length > 0 && filteredAndSortedTalents.length === 0 && (
        <div className="d-flex align-items-center justify-content-center mb-3">
          <p className="text-center">No talents match your search</p>
        </div>
      )}

      {/* Bulk Select Controls */}
      {!loading && filteredAndSortedTalents.length > 0 && (
        <div className="mb-4 p-3 border rounded bg-light">
          <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center">
              <input
                type="checkbox"
                id="select-all"
                checked={selectedTalents.length === filteredAndSortedTalents.length && filteredAndSortedTalents.length > 0}
                onChange={handleSelectAll}
                className="mr-2"
                style={{ width: "18px", height: "18px", cursor: "pointer", accentColor: "#3A6ED4" }}
              />
              <label htmlFor="select-all" className="mb-0 font-weight-bold">
                Select All ({selectedTalents.length} selected)
              </label>
            </div>
            {selectedTalents.length > 0 && (
              <CustomButton
                type="button"
                handleClick={handleBulkInvite}
                disabled={bulkInviting}
                backgroundColor="#3a6ed4"
                color="#fff"
                text={`Invite ${selectedTalents.length} Talent${selectedTalents.length > 1 ? "s" : ""}`}
                additionalClass="ms-2"
                loading={bulkInviting}
              />
            )}
          </div>
        </div>
      )}

      {/* Talents List */}
      {!loading &&
        filteredAndSortedTalents.length > 0 &&
        filteredAndSortedTalents.map((talent) => {
          const isSelected = selectedTalents.includes(talent.talent_id);
          return (
            <div
              key={talent.id}
              className={`d-flex align-items-center cursor-pointer justify-content-between mb-3 p-2 rounded ${
                isSelected ? "bg-light border border-primary" : ""
              }`}
            >
              <span className="d-flex align-items-center">
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => handleSelectTalent(talent.talent_id)}
                  onClick={(e) => e.stopPropagation()}
                  className="mr-3"
                  style={{ width: "18px", height: "18px", cursor: "pointer", accentColor: "#3A6ED4" }}
                  disabled={!talent.can_invite}
                />
                <img
                  width="50"
                  src={talent.image_url || "https://via.placeholder.com/50"}
                  alt="avatar"
                  style={{ filter: "blur(5px)", borderRadius: "50%" }}
                />
                <span className="d-flex flex-column ml-3">
                  <h6 className="mb-0">
                    {getFirstName(talent.name)}
                  </h6>
                  {talent.total_score !== undefined &&
                    talent.total_score !== null && (
                      <span className="co-gray small-text">
                        Match Score:{" "}
                        {typeof talent.total_score === "number"
                          ? `${Math.round(talent.total_score)}%`
                          : talent.total_score}
                      </span>
                    )}
                  {!talent.can_invite && talent.cooldown_remaining != null && talent.cooldown_remaining > 0 && (
                    <span className="small-text" style={{color: "#dc3545"}}>
                      Cooldown: {Math.floor(talent.cooldown_remaining)}h {Math.round((talent.cooldown_remaining % 1) * 60)}m remaining
                    </span>
                  )}
                </span>
              </span>

              <span>
                <CustomButton
                  type="button"
                  handleClick={() => handleInvite(talent)}
                  disabled={inviting[talent.talent_id] || !talent.can_invite}
                  text={talent.invited ? "Invited" : "Invite"}
                  alt="Invite"
                  backgroundColor={talent.invited ? "#e9ecef" : "white"}
                  color={talent.invited ? "#6c757d" : undefined}
                  borderColor={talent.invited ? "#dee2e6" : "var(--primary-color)"}
                  additionalClass="px-4"
                  loading={inviting[talent.talent_id]}
                />
              </span>
            </div>
          );
        })}
    </div>
  );
};

export default IdealTalents;
